import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Pencil, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import type {
  EpcProject,
  EpcProjectHealth as EpcProjectHealthState,
  EpcProjectMembershipRow,
} from '../../../services/epcProjectService';
import { ROLE_LABEL } from './team/teamCapabilities';
import { EPC_THEME_STYLES, SOVEREIGN, getEpcVisualTheme } from '../utils/epcProjectVisual';
import DepartmentTagStrip from './card/DepartmentTagStrip';
import { EpcProjectMiniIcon } from './card/EpcProjectIllustration';
import { deriveProjectProgress } from './card/epcProjectProgress';

interface EpcProjectListRowProps {
  project: EpcProject;
  canEdit: boolean;
  myMembership?: EpcProjectMembershipRow | null;
  index: number;
}

const HEALTH_STYLE: Record<EpcProjectHealthState, { ink: string; bg: string; dot: string; label: string }> = {
  on_track:      { ink: '#1F5A47', bg: 'rgba(31,90,71,0.12)',   dot: '#339274', label: 'On track' },
  at_risk:       { ink: '#8A5A08', bg: 'rgba(176,123,9,0.12)',  dot: '#C9A961', label: 'At risk' },
  delayed:       { ink: '#9F1239', bg: 'rgba(159,18,57,0.10)',  dot: '#B91C1C', label: 'Delayed' },
  completed:     { ink: '#0B3B2E', bg: 'rgba(11,59,46,0.10)',   dot: '#1F5A47', label: 'Completed' },
  upcoming:      { ink: '#5A6275', bg: 'rgba(90,98,117,0.10)',  dot: '#94A3B8', label: 'Upcoming' },
  not_configured:{ ink: '#5A6275', bg: 'rgba(90,98,117,0.08)', dot: '#94A3B8', label: 'Planning' },
};

function refName(ref: unknown): string {
  if (ref && typeof ref === 'object' && 'name' in ref) {
    return String((ref as { name?: string }).name ?? '');
  }
  return '';
}

function locationOf(project: EpcProject): { name: string; extra: number } | null {
  const locs = Array.isArray(project.locationIds) ? project.locationIds : [];
  const names = locs.map((loc) => refName(loc)).filter((n) => n.length > 0);
  if (names.length > 0) return { name: names[0], extra: names.length - 1 };
  const single = refName(project.locationId);
  return single ? { name: single, extra: 0 } : null;
}

function capacity(project: EpcProject): string | null {
  const ac = typeof project.acCapacityMw === 'number' ? project.acCapacityMw : NaN;
  if (Number.isFinite(ac) && ac > 0) return `${ac.toFixed(ac >= 10 ? 1 : 2)} MW`;
  const dc = typeof project.dcCapacityMw === 'number' ? project.dcCapacityMw : NaN;
  if (Number.isFinite(dc) && dc > 0) return `${dc.toFixed(dc >= 10 ? 1 : 2)} MW`;
  return null;
}

export default function EpcProjectListRow({ project, canEdit, myMembership, index }: EpcProjectListRowProps) {
  const theme = getEpcVisualTheme(project);
  const styles = EPC_THEME_STYLES[theme];
  const derived = deriveProjectProgress({ project });
  const health = HEALTH_STYLE[derived.health];
  const loc = locationOf(project);
  const customer = refName(project.customerId);
  const typeLabel = refName(project.projectTypeId);
  const cap = capacity(project);

  const plannedPct = Math.round(derived.plannedPct);
  const actualPct = derived.actualPct == null ? null : Math.round(derived.actualPct);

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index, 8) * 0.025 }}
      className="group/row relative grid grid-cols-12 items-center gap-3 rounded-xl px-3.5 py-3 transition-all duration-200"
      style={{
        fontFeatureSettings: '"tnum", "ss01"',
        background: SOVEREIGN.cardBg,
        border: `1px solid ${SOVEREIGN.cardBorder}`,
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(11,59,46,0.04)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = SOVEREIGN.cardBg; }}
    >
      {/* Left accent rail (health) */}
      <span
        aria-hidden
        className="pointer-events-none absolute left-0 top-3 bottom-3 w-1 rounded-r-full"
        style={{ background: health.ink }}
      />

      {/* COL 1-5: identity */}
      <div className="col-span-12 md:col-span-5 flex items-center gap-3 min-w-0">
        <span
          className="relative flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
          style={{ background: `linear-gradient(140deg, ${styles.accentSoft} 0%, transparent 100%)` }}
        >
          <EpcProjectMiniIcon variant={theme} accent={styles.accent} className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="font-mono text-[10px] font-semibold tracking-[0.14em] tabular-nums truncate uppercase"
              style={{ color: SOVEREIGN.ink }}
            >
              {project.code || '—'}
            </span>
            <span aria-hidden className="h-1 w-1 rounded-full flex-shrink-0" style={{ background: SOVEREIGN.cardBorder }} />
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.14em] truncate" style={{ color: SOVEREIGN.ink }}>
              {typeLabel || styles.label}
            </span>
          </div>
          <h3
            className="mt-0.5 text-[13.5px] font-semibold leading-tight tracking-tight truncate"
            style={{ color: SOVEREIGN.title }}
            title={project.name}
          >
            {project.name}
          </h3>
          <DepartmentTagStrip myMembership={myMembership ?? undefined} />
          <div className="mt-0.5 flex items-center gap-2 text-[11px] min-w-0" style={{ color: SOVEREIGN.kpiCaption }}>
            {loc && (
              <span className="inline-flex items-center gap-1 min-w-0 max-w-[55%]">
                <MapPin className="h-3 w-3 flex-shrink-0" style={{ color: SOVEREIGN.kpiCaption }} strokeWidth={2} />
                <span className="truncate">
                  {loc.name}
                  {loc.extra > 0 ? <span className="ml-1" style={{ color: SOVEREIGN.kpiCaption }}>+{loc.extra}</span> : null}
                </span>
              </span>
            )}
            {loc && customer && <span aria-hidden className="h-1 w-1 rounded-full" style={{ background: SOVEREIGN.cardBorder }} />}
            {customer && <span className="truncate">{customer}</span>}
          </div>
        </div>
      </div>

      {/* COL 6-7: capacity */}
      <div className="hidden md:flex col-span-2 items-center justify-start gap-1.5 text-[12px]" style={{ color: SOVEREIGN.title }}>
        {cap ? (
          <>
            <Zap className="h-3 w-3" style={{ color: SOVEREIGN.kpiCaption }} strokeWidth={2.4} />
            <span className="font-semibold tabular-nums">{cap}</span>
          </>
        ) : (
          <span style={{ color: SOVEREIGN.barTrack }}>—</span>
        )}
      </div>

      {/* COL 8-9: progress bar */}
      <div className="hidden md:block col-span-2">
        <div className="flex items-center gap-2">
          <div
            className="relative h-1.5 flex-1 overflow-hidden rounded-full"
            style={{ background: SOVEREIGN.barTrack, border: `1px solid ${SOVEREIGN.barTrackBorder}` }}
          >
            {/* Planned (lighter) */}
            <span
              aria-hidden
              className="absolute inset-y-0 left-0 rounded-full"
              style={{ width: `${plannedPct}%`, background: SOVEREIGN.barPlanned }}
            />
            {/* Actual (solid gradient) — overlays planned */}
            {actualPct != null && (
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 rounded-full"
                style={{ width: `${actualPct}%`, background: SOVEREIGN.barFill }}
              />
            )}
          </div>
          <span
            className="text-[10.5px] font-bold tabular-nums w-9 text-right"
            style={{ color: SOVEREIGN.bottle }}
            title={actualPct != null ? `Actual ${actualPct}% · Planned ${plannedPct}%` : `Planned ${plannedPct}%`}
          >
            {actualPct != null ? `${actualPct}%` : `${plannedPct}%`}
          </span>
        </div>
      </div>

      {/* COL 10: health pill */}
      <div className="hidden md:flex col-span-1 items-center justify-start">
        <span
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.10em]"
          style={{
            color: health.ink,
            backgroundColor: health.bg,
            border: `1px solid ${health.dot}40`,
          }}
        >
          <span aria-hidden className="h-1 w-1 rounded-full" style={{ background: health.dot }} />
          {health.label}
        </span>
      </div>

      {/* COL 11-12: actions */}
      <div className="col-span-12 md:col-span-2 flex items-center justify-end gap-1.5">
        {myMembership && (
          <span
            className="hidden xl:inline-flex items-center gap-1 rounded-md bg-slate-900/85 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-white"
            title={`You are ${ROLE_LABEL[myMembership.role]} on this project`}
          >
            {ROLE_LABEL[myMembership.role]}
          </span>
        )}
        {canEdit && (
          <Link
            to={`/epc/projects/${project._id}/edit`}
            title="Edit project"
            className="inline-flex items-center justify-center h-7 w-7 rounded-lg transition-all duration-200"
            style={{
              background: SOVEREIGN.inputBg,
              border: `1px solid ${SOVEREIGN.inputBorder}`,
              color: SOVEREIGN.title,
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = SOVEREIGN.inputHoverBorder; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = SOVEREIGN.inputBorder; }}
          >
            <Pencil className="h-3 w-3" strokeWidth={2.4} />
          </Link>
        )}
        <Link
          to={`/epc/projects/${project._id}`}
          className="group/btn inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-white transition-all duration-200"
          style={{
            background: SOVEREIGN.btn,
            border: `1px solid ${SOVEREIGN.btnBorder}`,
            boxShadow: SOVEREIGN.btnShadow,
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = SOVEREIGN.btnHover; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = SOVEREIGN.btn; }}
        >
          Open
          <ArrowRight
            className="h-3 w-3 transition-transform duration-200 group-hover/btn:translate-x-0.5"
            strokeWidth={2.6}
          />
        </Link>
      </div>
    </motion.div>
  );
}
